import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import { ICON_PX } from '../ui';
import NodeConfigPanel from './NodeConfigPanel';
import WorkflowAIChat from './WorkflowAIChat';

/**
 * Right sidebar for the Workflow Canvas.
 *
 * Layout:
 *   - AI Chat (always visible, takes remaining space)
 *   - Node Config accordion (build mode only, collapsed when no node selected)
 *
 * The completion form is NO LONGER here — it lives inline in the list view
 * (WorkflowExecutionList accordion) and in the canvas popover (NodeExecutionPopover).
 */
function WorkflowSidebar({
  mode = 'build',
  selectedNode,
  taskLibrary = [],
  workflows = [],
  swimlanes = [],
  onDeselectNode,
  onUpdateNode,
  onDeleteNode,
  // AI Chat props
  chatMessages = [],
  chatIsLoading = false,
  onChatSendMessage,
  onChatApply,
  onChatRevert,
}) {
  const [configExpanded, setConfigExpanded] = useState(true);
  const isBuild = mode === 'build';
  const hasSelectedNode = isBuild && selectedNode;

  return (
    <div className="flex flex-col h-full border-l border-slate-200 bg-white rounded-r-xl overflow-hidden">
      {/* ── Node Config Accordion (build mode only, when node selected) ── */}
      {hasSelectedNode && (
        <div className="shrink-0 border-b border-slate-100">
          {/* Accordion header */}
          <button
            type="button"
            onClick={() => setConfigExpanded((prev) => !prev)}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
          >
            <Settings2 size={ICON_PX.sm} className="text-slate-400 shrink-0" />
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex-1">
              Node Config
            </span>
            <span className="text-[10px] text-slate-400 truncate max-w-[100px] mr-1">
              {selectedNode.data?.label || 'Untitled'}
            </span>
            {configExpanded
              ? <ChevronDown size={ICON_PX.sm} className="text-slate-400 shrink-0" />
              : <ChevronRight size={ICON_PX.sm} className="text-slate-400 shrink-0" />
            }
          </button>

          {/* Accordion body */}
          {configExpanded && (
            <div className="max-h-[45vh] overflow-y-auto">
              <NodeConfigPanel
                selectedNode={selectedNode}
                taskLibrary={taskLibrary}
                workflows={workflows}
                swimlanes={swimlanes}
                onUpdateNode={onUpdateNode}
                onDeleteNode={(nodeId) => {
                  onDeleteNode?.(nodeId);
                  onDeselectNode?.();
                }}
                onDeselectNode={onDeselectNode}
              />
            </div>
          )}
        </div>
      )}

      {/* ── AI Chat (always visible, fills remaining space) ── */}
      <div className="flex-1 min-h-0">
        <WorkflowAIChat
          messages={chatMessages}
          isLoading={chatIsLoading}
          onSendMessage={onChatSendMessage}
          onApply={onChatApply}
          onRevert={onChatRevert}
          mode={mode}
        />
      </div>
    </div>
  );
}

export default WorkflowSidebar;
